import { UpgradeTemplatesTableElement, fromApiResponses } from './data-model';
import { UpgradeTemplateDetails } from './details';
import { UpgradeBadgeStatus } from './helpers';
import { PropertyFilterToken } from '@cloudscape-design/collection-hooks';
import { Button, ContentLayout, Header } from '@cloudscape-design/components';
import { useGlobalListUpgradeTemplatesInfinite } from 'api/admin-infinite';
import { Auth } from 'aws-amplify';
import { CognitoUser } from '@aws-amplify/auth';
import { FilterableTable } from 'components/tables/filterable-table';
import { useSplitPanel } from 'contexts/split-panel';
import { formatISO } from 'date-fns';
import CopyText from 'components/copy-text';

export function UpgradeTemplatesIndex(): JSX.Element {
  const updateSplitPanel = useSplitPanel();
  const { data, mutate } = useGlobalListUpgradeTemplatesInfinite();
  const isGamma = import.meta.env.VITE_API_ENDPOINT.includes('chkk.dog');

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          actions={
            <Button
              iconName="refresh"
              variant="icon"
              onClick={() => void mutate()}
            />
          }
        >
          Upgrade Templates
        </Header>
      }
    >
      <FilterableTable<UpgradeTemplatesTableElement>
        variant={'container'}
        selectionMode="single"
        data={fromApiResponses(data)}
        columns={[
          {
            id: 'AccountId',
            label: 'Account ID',
            cell: (item) => (
              <CopyText
                copyText={item.AccountId}
                copyButtonLabel="Click to copy"
                successText="Copied"
                errorText="Failed to copy"
              />
            ),

            defaultVisible: true,
            alwaysVisible: true,

            sortingField: 'AccountId',
            filterOperators: ['=', '!=', ':', '!:'],
          },
          {
            id: 'Id',
            label: 'ID',
            cell: (item) => (
              <CopyText
                copyText={item.Id}
                copyButtonLabel="Click to copy"
                successText="Copied"
                errorText="Failed to copy"
              />
            ),

            defaultVisible: true,

            sortingField: 'Id',
            filterOperators: ['=', '!=', ':', '!:'],
          },
          {
            id: 'Name',
            label: 'Name',
            cell: (item) => item.Name || '-',

            defaultVisible: true,

            sortingField: 'Name',
            filterOperators: ['=', '!=', ':', '!:'],
          },
          {
            id: 'Environment',
            label: 'Environment',
            cell: (item) => item.Environment || '-',

            defaultVisible: !isGamma,

            sortingField: 'Environment',
            filterOperators: ['=', '!=', ':', '!:'],
          },
          {
            id: 'ClusterId',
            label: 'Cluster ID',
            cell: (item) => (
              <CopyText
                copyText={item.ClusterId || '-'}
                copyButtonLabel="Click to copy"
                successText="Copied"
                errorText="Failed to copy"
              />
            ),

            defaultVisible: true,

            sortingField: 'ClusterId',
            filterOperators: ['=', '!=', ':', '!:'],
          },
          {
            id: 'Status',
            label: 'Status',
            cell: (item) => (
              <UpgradeBadgeStatus value={item.Status || 'pending'} />
            ),

            defaultVisible: true,

            sortingField: 'Status',
            filterOperators: ['=', '!='],
            filterOptions: [
              'pending',
              'in-progress',
              'available',
              'completed',
              'cancellation-in-progress',
              'cancelled',
              'outdated',
              'update-in-progress',
              'regenerate-in-progress',
              'approved',
            ],
          },
          {
            id: 'CreationStatus',
            label: 'Creation Status',
            cell: (item) => (
              <UpgradeBadgeStatus
                value={item.CreationStatus || 'pending-assignment'}
              />
            ),

            defaultVisible: true,

            sortingField: 'CreationStatus',
            filterOperators: ['=', '!='],
            filterOptions: [
              'pending-assignment',
              'in-progress',
              'in-review',
              'published',
            ],
          },
          {
            id: 'Assignee',
            label: 'Assignee',
            cell: (item) => item.Assignee || '-',
            sortingField: 'Assignee',

            defaultVisible: !isGamma,

            filterOperators: ['=', '!='],
          },
          {
            id: 'Creation',
            label: 'Created',
            cell: (item) => formatISO(new Date(item.Created)),

            defaultVisible: true,

            sortingField: 'Created',
          },
        ]}
        defaultSorting={{ field: 'Creation', direction: 'descending' }}
        defaultFilterQuery={async (searchParams) => {
          const user = (await Auth.currentAuthenticatedUser()) as CognitoUser;
          const session = user.getSignInUserSession();
          const tokens: PropertyFilterToken[] = [];

          if (searchParams.get('mine') === 'true') {
            tokens.push({
              propertyKey: 'Assignee',
              value: session?.getIdToken().payload.email as string,
              operator: '=',
            });
          }
          if (searchParams.get('status') === 'pending') {
            tokens.push({
              propertyKey: 'Status',
              value: searchParams.get('status'),
              operator: '=',
            });
          }
          return { tokens: tokens, operation: 'and' };
        }}
        selectionTrackBy={(item) => `${item.AccountId}#${item.Id}`}
        onSelectionChange={(event) => {
          updateSplitPanel(
            <UpgradeTemplateDetails
              key={`${event.detail.selectedItems[0].AccountId}:${event.detail.selectedItems[0].Id}`}
              upgrade={{
                orgId: '',
                accountId: event.detail.selectedItems[0].AccountId,
                upgradeId: event.detail.selectedItems[0].Id,
              }}
              onChange={() => void mutate()}
            />,
          );
        }}
        header={<Header />}
      />
    </ContentLayout>
  );
}
